import { memo } from 'react';
import { Form, Input, Select } from 'antd';
import { cn } from '@/libs/utils';
import { COMPANY_GENERAL_DATA_FORM, MARKET_OPTIONS } from '@/shared/company';
import { translateOptions } from '@/utils/utils';
import CompanyGeneralDataFormContext from './company-general-data/CompanyGeneralDataFormContext';
import CompanyAccessAssignments from './company-general-data/sections/CompanyAccessAssignments';
import CompanyAllowancesFields from './company-general-data/sections/CompanyAllowancesFields';
import CompanyAllowancesProfilePanel from './company-general-data/sections/CompanyAllowancesProfilePanel';
import CompanyBonusFields from './company-general-data/sections/CompanyBonusFields';
import CompanyBonusProfilePanel from './company-general-data/sections/CompanyBonusProfilePanel';
import CompanyContactFields from './company-general-data/sections/CompanyContactFields';
import CompanyEInvoiceProfilePanel from './company-general-data/sections/CompanyEInvoiceProfilePanel';
import CompanyEInvoiceSettings from './company-general-data/sections/CompanyEInvoiceSettings';
import CompanyEmailReceiversFields from './company-general-data/sections/CompanyEmailReceiversFields';
import CompanyFormFooter from './company-general-data/sections/CompanyFormFooter';
import CompanyFormSidebars from './company-general-data/sections/CompanyFormSidebars';
import CompanyHrProfilePanel from './company-general-data/sections/CompanyHrProfilePanel';
import CompanyHrRulesFields from './company-general-data/sections/CompanyHrRulesFields';
import CompanyHrSettings from './company-general-data/sections/CompanyHrSettings';
import CompanyIdentityFields from './company-general-data/sections/CompanyIdentityFields';
import CompanyModulesAccessProfilePanel from './company-general-data/sections/CompanyModulesAccessProfilePanel';
import CompanyModulesAccessSettings from './company-general-data/sections/CompanyModulesAccessSettings';
import CompanyOwnershipFields from './company-general-data/sections/CompanyOwnershipFields';
import CompanyPayrollFields from './company-general-data/sections/CompanyPayrollFields';
import CompanyPayrollProfilePanel from './company-general-data/sections/CompanyPayrollProfilePanel';
import CompanyTimeProfilePanel from './company-general-data/sections/CompanyTimeProfilePanel';
import CompanyTimeSettings from './company-general-data/sections/CompanyTimeSettings';
import CompanyTypeFields from './company-general-data/sections/CompanyTypeFields';
import useCompanyCalculationFields from './company-general-data/useCompanyCalculationFields';
import useCompanyFormActions from './company-general-data/useCompanyFormActions';
import useCompanyFormBase from './company-general-data/useCompanyFormBase';
import useCompanyFormSideState from './company-general-data/useCompanyFormSideState';
import useCompanyGeneralFields from './company-general-data/useCompanyGeneralFields';

function CompanyGeneralDataForm({
  formData,
  isEdited,
  className,
  companyId,
  // Profile page layout: one mounted form, only the selected tile's fields
  // shown (the others hidden, never unmounted). See EmployeeGeneralDataForm.
  profileSection = null,
  readOnly = false,
  form: formProp,
  onSaved,
  onCancel,
  onFinishFailed,
  onDirtySectionsChange,
  // Profile page: receives { save, discard } of the open tile.
  actionsRef,
}) {
  const props = {
    actionsRef,
    className,
    companyId,
    formData,
    formProp,
    isEdited,
    onCancel,
    onDirtySectionsChange,
    onFinishFailed,
    onSaved,
    profileSection,
    readOnly,
  };
  const companyFormBase = useCompanyFormBase({ ...props });
  const companyFormSideState = useCompanyFormSideState({
    ...props,
    ...companyFormBase,
  });
  const companyGeneralFields = useCompanyGeneralFields({
    ...props,
    ...companyFormBase,
    ...companyFormSideState,
  });
  const companyCalculationFields = useCompanyCalculationFields({
    ...props,
    ...companyFormBase,
    ...companyFormSideState,
    ...companyGeneralFields,
  });
  const companyFormActions = useCompanyFormActions({
    ...props,
    ...companyFormBase,
    ...companyFormSideState,
    ...companyGeneralFields,
    ...companyCalculationFields,
  });
  const sectionContext = {
    ...props,
    ...companyFormBase,
    ...companyFormSideState,
    ...companyGeneralFields,
    ...companyCalculationFields,
    ...companyFormActions,
  };
  const {
    form,
    isProfile,
    marketKeys,
    marketState,
    onFinish,
    sectionClass,
    t,
  } = sectionContext;

  return (
    <CompanyGeneralDataFormContext.Provider value={sectionContext}>
      <>
        <Form
          form={form}
          name="company_general_data_form"
          initialValues={{
            ...COMPANY_GENERAL_DATA_FORM,
          }}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          disabled={readOnly}
          className={cn(
            'form p-4',
            isProfile && 'employee-profile-form',
            readOnly && 'employee-profile-form--readonly',
            className
          )}
        >
          {/* On the profile a plain field like the others, not a callout. */}
          <div
            className={cn(
              !isProfile &&
                'mb-5 rounded-md border-2 border-blue-200 bg-blue-50 p-4',
              sectionClass('osnovno')
            )}
          >
            <Form.Item
              name={marketKeys}
              label={
                isProfile ? (
                  t('market.label', { ns: 'forms' })
                ) : (
                  <span className="text-sm font-semibold text-blue-800">
                    {t('market.label', { ns: 'forms' })}
                  </span>
                )
              }
              className={cn(!isProfile && '!mb-0')}
              initialValue="RS"
            >
              <Select
                className="w-full"
                disabled={marketState?.disabled}
                options={translateOptions(MARKET_OPTIONS, t)}
                size={isProfile ? undefined : 'large'}
              />
            </Form.Item>
          </div>

          <div
            className={cn(
              !isProfile && 'mb-5 grid rounded-md border bg-zinc-50 p-4'
            )}
          >
            {isProfile && <CompanyHrProfilePanel />}

            <CompanyHrSettings />

            {isProfile && <CompanyEInvoiceProfilePanel />}

            <CompanyEInvoiceSettings />

            {isProfile && <CompanyTimeProfilePanel />}

            <CompanyTimeSettings />

            {isProfile && <CompanyModulesAccessProfilePanel />}

            <CompanyModulesAccessSettings />
          </div>

          <CompanyIdentityFields />

          <CompanyOwnershipFields />

          <CompanyContactFields />

          {/* On the profile it is part of CompanyModulesAccessProfilePanel. */}
          {!isProfile && <CompanyAccessAssignments />}

          <CompanyTypeFields />

          {isProfile && <CompanyAllowancesProfilePanel />}

          <CompanyAllowancesFields />

          {isProfile && <CompanyPayrollProfilePanel />}

          <CompanyPayrollFields />

          {isProfile && <CompanyBonusProfilePanel />}

          <CompanyBonusFields />

          <CompanyHrRulesFields />

          <CompanyEmailReceiversFields />

          <CompanyFormFooter />

          {/* Hidden field for signature */}
          <Form.Item name={['authorizedRepresentatives', 'signature']} hidden>
            <Input />
          </Form.Item>
        </Form>

        <CompanyFormSidebars />
      </>
    </CompanyGeneralDataFormContext.Provider>
  );
}

// A tile change on the profile re-renders the whole form; memo keeps the
// profile's other updates (the tile mark, the header) from doing the same.
export default memo(CompanyGeneralDataForm);
